import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, TextField, CircularProgress, Paper, List, ListItem, ListItemButton,
  ListItemText, Chip, Typography, InputAdornment, IconButton,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import productoService from '../../../services/productoService';
import articuloXListaPrecioService from '../../../services/articuloXListaPrecioService';
import inventarioInformes from '../../../services/inventarioInformes';
import { resolverPrecioDesdeLista, obtenerPrecioBaseProducto } from '../../../utils/precioListaPedido';
import { formatCurrency } from '../../../utils/formatters';
import { SuggestionItem } from './types';

export type { SuggestionItem } from './types';

interface Props {
  nroLista?: string;
  disabled?: boolean;
  onAgregarProducto: (item: SuggestionItem) => void;
}

const StockBadge: React.FC<{ stock: number | null | undefined }> = ({ stock }) => {
  if (stock == null) return null;
  if (stock <= 0)
    return <Chip label="Sin stock" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />;
  if (stock < 10)
    return <Chip label={`Stock: ${stock}`} size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />;
  return <Chip label={`Stock: ${stock}`} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />;
};

const FacturaPOSSearchBar: React.FC<Props> = ({ nroLista, disabled, onAgregarProducto }) => {
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const buscarProductos = useCallback(async (q: string) => {
    if (!q.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await productoService.getProductos({ searchTerm: q, pageNumber: 1, pageSize: 8 });
      const items: any[] = res.items ?? [];

      const enriched: SuggestionItem[] = await Promise.all(
        items.map(async (p) => {
          let precio = obtenerPrecioBaseProducto(p);

          if (nroLista) {
            try {
              const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(
                nroLista,
                p.codigoArticulo,
              );
              const resultado = resolverPrecioDesdeLista(p, reg);
              if (resultado.tipo !== 'sinPrecio') precio = resultado.precioLista;
            } catch {
              /* sin precio en lista, usa precio base */
            }
          }

          let stock: number | null = null;
          try {
            const stockData = await inventarioInformes.getStockPorDeposito({
              codigoArticulo: p.codigoArticulo,
              pageSize: 50,
            });
            const rows: any[] = (stockData?.data ?? []).filter(
              (r: any) => (r.codigoArticulo ?? '').trim() === p.codigoArticulo.trim(),
            );
            if (rows.length > 0) {
              stock = rows.reduce(
                (acc: number, r: any) => acc + (Number(r.disponibleDeposito) || 0),
                0,
              );
            }
          } catch {
            /* sin stock disponible */
          }

          return {
            codigoArticulo: p.codigoArticulo,
            descripcion: p.descripcion,
            precioUnitario: precio,
            um: p.um ?? p.unidadMedida ?? '',
            stockDisponible: stock,
          };
        }),
      );

      setSuggestions(enriched);
      setOpen(enriched.length > 0);
    } catch {
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [nroLista]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarProductos(term), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, buscarProductos]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item: SuggestionItem) => {
    onAgregarProducto(item);
    setTerm('');
    setSuggestions([]);
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && suggestions.length === 1) {
      handleSelect(suggestions[0]);
    }
    if (e.key === 'Escape') setOpen(false);
  };

  const handleClear = () => {
    setTerm('');
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        placeholder="Código o descripción… Enter para agregar el primero"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        disabled={disabled}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {loading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" color="action" />}
            </InputAdornment>
          ),
          endAdornment: term ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} edge="end">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
      />

      {open && suggestions.length > 0 && (
        <Paper
          elevation={6}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1400,
            maxHeight: 340,
            overflowY: 'auto',
            mt: 0.5,
            borderRadius: 2,
          }}
        >
          <List dense disablePadding>
            {suggestions.map((s, idx) => (
              <ListItem key={s.codigoArticulo} disablePadding divider={idx < suggestions.length - 1}>
                <ListItemButton onClick={() => handleSelect(s)} sx={{ py: 0.75, px: 1.5 }}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ minWidth: 80, fontFamily: 'monospace', flexShrink: 0 }}
                        >
                          {s.codigoArticulo}
                        </Typography>
                        <Typography variant="body2" flex={1} noWrap>
                          {s.descripcion}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box display="flex" alignItems="center" gap={1} mt={0.2}>
                        <Typography variant="caption" color="primary.main" fontWeight={600}>
                          {formatCurrency(s.precioUnitario)}
                        </Typography>
                        {s.um && (
                          <Typography variant="caption" color="text.secondary">
                            / {s.um}
                          </Typography>
                        )}
                        <StockBadge stock={s.stockDisponible} />
                      </Box>
                    }
                    disableTypography
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default FacturaPOSSearchBar;
